import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { 
  Leaf, ArrowLeft, Calendar, TrendingUp, TrendingDown, 
  Minus, Loader2, Check, Plus, ChevronLeft, ChevronRight,
  Activity, Moon, Smile, Brain, Flame, Thermometer, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate, todayLocal, daysAgoLocal } from '../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYMPTOM_ICONS = {
  energy: Activity,
  sleep_quality: Moon,
  mood: Smile,
  stress: Brain,
  focus: Brain,
  hot_flashes: Flame,
  night_sweats: Thermometer,
  irritability: Heart,
  joint_pain: Activity,
  bloating: Activity,
  digestion: Activity,
  headache: Brain,
  muscle_pain: Activity,
};

export default function SymptomTrackingPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  
  const [currentDate, setCurrentDate] = useState(todayLocal());
  const [categories, setCategories] = useState({});
  const [symptoms, setSymptoms] = useState({});
  const [notes, setNotes] = useState('');
  const [supplementsTaken, setSupplementsTaken] = useState([]);
  const [availableSupplements, setAvailableSupplements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [trends, setTrends] = useState(null);
  const [activeTab, setActiveTab] = useState('log'); // 'log' or 'trends'
  const [selectedCategory, setSelectedCategory] = useState('general');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/symptoms');
    }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [catRes, logRes, assessRes] = await Promise.all([
        axios.get(`${API}/symptoms/categories`),
        axios.get(`${API}/symptoms/${currentDate}`, { headers: getAuthHeader() }),
        axios.get(`${API}/assessments`, { headers: getAuthHeader() })
      ]);
      
      setCategories(catRes.data.categories);
      
      // Initialize symptoms from categories
      const initialSymptoms = {};
      Object.values(catRes.data.categories).flat().forEach(s => {
        initialSymptoms[s.id] = s.id === 'cycle_day' ? 1 : (s.min + s.max) / 2;
      });
      
      if (logRes.data.log) {
        setSymptoms({ ...initialSymptoms, ...logRes.data.log.symptoms });
        setNotes(logRes.data.log.notes || '');
        setSupplementsTaken(logRes.data.log.supplements_taken || []);
      } else {
        setSymptoms(initialSymptoms);
        setNotes('');
        setSupplementsTaken([]);
      }
      
      // Get supplements from latest assessment
      if (assessRes.data.length > 0) {
        const supplements = assessRes.data[0].recommended_supplements?.map(s => s.name) || [];
        setAvailableSupplements(supplements);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [user, currentDate, getAuthHeader]);

  const fetchTrends = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await axios.get(`${API}/symptoms/trends?days=30`, { headers: getAuthHeader() });
      setTrends(res.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'trends') {
      fetchTrends();
    }
  }, [activeTab, fetchTrends]);

  const handleSymptomChange = (symptomId, value) => {
    setSymptoms(prev => ({ ...prev, [symptomId]: value[0] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/symptoms`, {
        date: currentDate,
        symptoms,
        notes,
        supplements_taken: supplementsTaken
      }, { headers: getAuthHeader() });
      
      toast.success('Symptoms logged!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSupplement = (supp) => {
    setSupplementsTaken(prev => 
      prev.includes(supp) 
        ? prev.filter(s => s !== supp)
        : [...prev, supp]
    );
  };

  const changeDate = (delta) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + delta);
    if (date <= new Date()) {
      setCurrentDate(formatLocalDate(date));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = todayLocal();
    const yesterday = daysAgoLocal(1);
    
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'worsening') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const categorySymptoms = categories[selectedCategory] || [];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Title & Tabs */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-[#1A1A1A]">Symptom Tracking</h1>
            <p className="text-muted-foreground">Track how you feel to see supplement effects</p>
          </div>
          <div className="flex gap-2 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('log')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'log' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
            >
              Log
            </button>
            <button
              onClick={() => setActiveTab('trends')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'trends' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
            >
              Trends
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'log' ? (
            <motion.div
              key="log"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Date Selector */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <button
                  onClick={() => changeDate(-1)}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-border">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="font-medium">{formatDate(currentDate)}</span>
                </div>
                <button
                  onClick={() => changeDate(1)}
                  disabled={currentDate === todayLocal()}
                  className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Category Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {Object.keys(categories).map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === cat 
                        ? 'bg-primary text-white' 
                        : 'bg-white border border-border text-[#1A1A1A] hover:border-primary/30'
                    }`}
                  >
                    {cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                  </button>
                ))}
              </div>

              {/* Symptom Sliders */}
              <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                <h2 className="font-semibold text-lg mb-6 capitalize">
                  {selectedCategory.replace('_', ' ')} Symptoms
                </h2>
                <div className="space-y-8">
                  {categorySymptoms.map(symptom => {
                    const Icon = SYMPTOM_ICONS[symptom.id] || Activity;
                    return (
                      <div key={symptom.id}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5 text-primary" />
                            <span className="font-medium">{symptom.name}</span>
                          </div>
                          <span className="text-lg font-semibold text-primary">
                            {symptoms[symptom.id] || symptom.min}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{symptom.description}</p>
                        <Slider
                          value={[symptoms[symptom.id] || symptom.min]}
                          onValueChange={(val) => handleSymptomChange(symptom.id, val)}
                          min={symptom.min}
                          max={symptom.max}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>{symptom.min}</span>
                          <span>{symptom.max}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Supplements Taken */}
              {availableSupplements.length > 0 && (
                <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                  <h2 className="font-semibold text-lg mb-4">Supplements Taken Today</h2>
                  <div className="flex flex-wrap gap-2">
                    {availableSupplements.map(supp => (
                      <button
                        key={supp}
                        onClick={() => toggleSupplement(supp)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          supplementsTaken.includes(supp)
                            ? 'bg-primary text-white'
                            : 'bg-muted text-muted-foreground hover:bg-primary/10'
                        }`}
                      >
                        {supplementsTaken.includes(supp) && <Check className="w-3 h-3 inline mr-1" />}
                        {supp}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                <h2 className="font-semibold text-lg mb-4">Notes</h2>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="How are you feeling today? Any observations..."
                  className="min-h-[100px] resize-none"
                />
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full h-12 btn-primary"
                data-testid="save-symptoms"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Today's Log
                  </>
                )}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="trends"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {!trends || trends.trends.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No trends yet</h2>
                  <p className="text-muted-foreground mb-6">
                    Log symptoms for a few days to see your trends
                  </p>
                  <Button onClick={() => setActiveTab('log')} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Log Today's Symptoms
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-white rounded-2xl border border-border p-6">
                    <h2 className="font-semibold text-lg mb-4">30-Day Summary</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-muted rounded-xl">
                        <p className="text-2xl font-bold text-primary">{trends.total_logs}</p>
                        <p className="text-sm text-muted-foreground">Days Logged</p>
                      </div>
                      <div className="text-center p-4 bg-muted rounded-xl">
                        <p className="text-2xl font-bold text-primary">{trends.trends.length}</p>
                        <p className="text-sm text-muted-foreground">Symptoms Tracked</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-xl">
                        <p className="text-2xl font-bold text-green-600">
                          {trends.trends.filter(t => t.trend === 'improving').length}
                        </p>
                        <p className="text-sm text-muted-foreground">Improving</p>
                      </div>
                      <div className="text-center p-4 bg-muted rounded-xl">
                        <p className="text-2xl font-bold text-primary">
                          {trends.supplements_logged?.length || 0}
                        </p>
                        <p className="text-sm text-muted-foreground">Supplements</p>
                      </div>
                    </div>
                  </div>

                  {/* Trend Cards */}
                  <div className="grid gap-4">
                    {trends.trends.map(trend => {
                      const Icon = SYMPTOM_ICONS[trend.symptom] || Activity;
                      return (
                        <div key={trend.symptom} className="bg-white rounded-xl border border-border p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium capitalize">
                                  {trend.symptom.replace(/_/g, ' ')}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Avg: {trend.average} / {trend.data.length} logs
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getTrendIcon(trend.trend)}
                              <span className={`text-sm font-medium capitalize ${
                                trend.trend === 'improving' ? 'text-green-600' :
                                trend.trend === 'worsening' ? 'text-red-600' : 'text-gray-500'
                              }`}>
                                {trend.trend}
                              </span>
                            </div>
                          </div>
                          
                          {/* Mini sparkline */}
                          <div className="mt-4 flex items-end gap-1 h-12">
                            {trend.data.slice(-14).map((point, i) => (
                              <div
                                key={i}
                                className="flex-1 bg-primary/20 rounded-t"
                                style={{ height: `${(point.value / 10) * 100}%` }}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
